import moment from 'moment';
import Constants from '../../utils/constants';
import {
  ChannelsResponse,
  CurrentEpgResponse,
  EpgResponse,
  FavoritesResponse,
  MainChannelsResponse,
  SearchEpgResult,
  SearchHistoryItem,
  SearchResult,
  UrlResponse,
} from '../models/tv';
import sendRequest, { getSession } from '../response';
import { ApiError } from '../../types/errors';
import Device from '../../common/device';

export async function mainChannels(): Promise<MainChannelsResponse> {
  const [, s] = getSession();
  const url = `${Constants.apiBaseUrlV3}/main-channels?sid=${s}&icon=png`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (res instanceof Object && res.status === 'error')
    throw new ApiError(res.code, res.message);

  return res;
}

export async function channelList(
  parentCode?: string,
): Promise<ChannelsResponse> {
  const [sn, s] = getSession();
  let url = `${Constants.apiBaseUrl}/channel_list?${sn}=${s}&icon=png`;
  if (parentCode) url += `&show=all&protect_code=${parentCode}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);

  return res;
}

export async function favorites(): Promise<FavoritesResponse> {
  const [sn, s] = getSession();
  const url = `${Constants.apiBaseUrl}/favorites?${sn}=${s}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);

  return res;
}

export async function setFavorite(id: number, place?: number) {
  const [sn, s] = getSession();
  let url = `${Constants.apiBaseUrl}/favorites_set?${sn}=${s}&cid=${id}`;
  if (place !== undefined) url += `&place=${place}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);
}

export async function setFavoriteList(ids: number[]) {
  const [sn, s] = getSession();
  const url =
    `${Constants.apiBaseUrl}/favorites_set_all?${sn}=${s}` +
    `&cids=${ids.join(',')}`;
  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);
}

export async function epg(
  channelId: number,
  date: number,
): Promise<EpgResponse> {
  const [sn, s] = getSession();

  const d = moment.unix(date).format('DDMMYY');
  const res = await sendRequest(
    `${Constants.apiBaseUrl}/epg?${sn}=${s}&mode=1&cid=${channelId}&day=${d}`,
    { ...Device.getDeviceHeaders() },
  );
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);

  return res;
}

export async function currentEpg(ids: number[]): Promise<CurrentEpgResponse> {
  const [sn, s] = getSession();
  const res = await sendRequest(
    `${Constants.apiBaseUrl}/epg_current?${sn}=${s}&cids=${ids.join(',')}`,
    { ...Device.getDeviceHeaders() },
  );
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);

  return res;
}

export async function url(
  channelId: number,
  dateTime?: number,
  parentCode?: string,
): Promise<UrlResponse> {
  const [sn, s] = getSession();

  let url = `${Constants.apiBaseUrl}/get_url?${sn}=${s}&cid=${channelId}`;
  if (dateTime) url += `&gmt=${Math.floor(dateTime)}`;
  if (parentCode) url += `&protect_code=${parentCode}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);

  return res;
}

export async function search(query: string): Promise<SearchResult> {
  const [, s] = getSession();
  const url = `${Constants.apiBaseUrlV3}/search?sid=${s}&q=${query}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if (res.status === 'error')
    throw new ApiError(res.error.code, res.error.message);

  return res;
}

export async function searchByType<T extends keyof SearchResult>(
  query: string,
  page = 1,
  type: T,
  subtype?: number,
): Promise<Pick<SearchResult, T>> {
  const [, s] = getSession();
  let url =
    `${Constants.apiBaseUrlV3}/search?sid=${s}&q=${query}` +
    `&page=${page}&index=${type}`;
  if (subtype !== undefined) url += `&type=${subtype}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if (res.status === 'error')
    throw new ApiError(res.error.code, res.error.message);

  return res;
}

export async function searchEpg(
  query: string,
  page = 1,
  channelId?: number,
): Promise<SearchEpgResult> {
  const [, s] = getSession();
  let url =
    `${Constants.apiBaseUrlV3}/search?sid=${s}&q=${query}` +
    `&page=${page}&index=epg&extend=1`;
  if (channelId !== undefined) url += `&channel=${channelId}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if (res.status === 'error')
    throw new ApiError(res.error.code, res.error.message);

  return res;
}

export async function searchHistory(): Promise<SearchHistoryItem[]> {
  const [, s] = getSession();
  const url = `${Constants.apiBaseUrlV3}/search-history?sid=${s}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (res instanceof Object && res.status === 'error')
    throw new ApiError(res.error.code, res.error.message);
  if (!Array.isArray(res)) throw new ApiError(-2, 'Wrong response');

  return res;
}

export async function addSearchHistory(query: string) {
  const [, s] = getSession();
  const url = `${Constants.apiBaseUrlV3}/search-history?sid=${s}`;

  const res = await sendRequest(
    url,
    {
      ...Device.getDeviceHeaders(),
      'Content-Type': 'application/json;charset=UTF-8',
    },
    JSON.stringify({ q: query }),
  );
  if (res instanceof Object && res.status === 'error')
    throw new ApiError(res.error.code, res.error.message);
}

export async function removeFromSearchHistory(id: number) {
  const [, s] = getSession();
  const url = `${Constants.apiBaseUrlV3}/search-history?sid=${s}&id=${id}`;

  const res = await sendRequest(
    url,
    { ...Device.getDeviceHeaders() },
    undefined,
    'DELETE',
  );
  if (res instanceof Object && res.status === 'error')
    throw new ApiError(res.error.code, res.error.message);
}

type RuleCommand = 'hide_channel' | 'show_channel' | 'reset_channels';

export async function rule(cmd: RuleCommand, parentCode: string, id?: number) {
  const [sn, s] = getSession();
  let url =
    `${Constants.apiBaseUrl}/rule?${sn}=${s}&cmd=${cmd}` +
    `&protect_code=${parentCode}`;
  if (id !== undefined) url += `&cid=${id}`;

  const res = await sendRequest(url, { ...Device.getDeviceHeaders() });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);
}

export async function info(
  type: string,
): Promise<{ title: string; text: string }> {
  const res = await sendRequest(`${Constants.apiBaseUrlV3}/info/${type}`, {
    ...Device.getDeviceHeaders(),
  });
  if (!(res instanceof Object)) throw new ApiError(-2, 'Wrong response');
  if ('error' in res) throw new ApiError(res.error.code, res.error.message);
  return res;
}
